import React from 'react'
import type { AppNotification } from '../../types/notifications'
import { isUnread } from '../../services/notifications'
import CountryFlagIcon from '../ui/CountryFlagIcon'
import {
  formatNotificationWhen,
  notificationSummaryClass,
  notificationTitleClass,
} from '../../utils/notificationPresentation'

interface NotificationItemContentProps {
  notification: AppNotification
  /** Dropdown rows use tighter typography and omit the timestamp. */
  compact?: boolean
  title?: React.ReactNode
}

/**
 * Shared read/unread presentation: signal dot + weight + muted copy for læst.
 */
const NotificationItemContent: React.FC<NotificationItemContentProps> = ({
  notification,
  compact = false,
  title,
}) => {
  const unread = isUnread(notification)

  return (
    <div className="flex gap-2.5 min-w-0">
      <span
        className={`shrink-0 flex justify-center ${compact ? 'mt-1' : 'mt-1.5'}`}
        style={{ width: '0.5rem' }}
        aria-hidden
      >
        {unread && <span className="block h-1.5 w-1.5 rounded-full bg-signal" />}
      </span>

      <div className="min-w-0 flex-1">
        <span className="sr-only">{unread ? 'Ulæst. ' : 'Læst. '}</span>
        {title ?? (
          <p className={notificationTitleClass(unread, compact)}>{notification.title}</p>
        )}
        <p className={notificationSummaryClass(unread, compact)}>
          {typeof notification.payload?.countryCode === 'string' && (
            <CountryFlagIcon
              code={notification.payload.countryCode}
              focusable={false}
              className="mr-1.5"
            />
          )}
          {notification.summary}
        </p>
        {!compact && (
          <p className="tabular-nums text-xs text-ink-400 mt-1.5">
            {formatNotificationWhen(notification.createdAt)}
          </p>
        )}
      </div>
    </div>
  )
}

export default NotificationItemContent
